<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Chapter' : 'Add Chapter'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <page-separator title="Chapter Course" />
      <!-- Course Selection -->
      <b-form-group label="Course" label-for="course" label-class="form-label">
        <v-select
          required
          id="course"
          class="form-control v-select-custom"
          label="title"
          v-model="chapter.course"
          :reduce="item => item.id"
          placeholder="Select Course"
          :options="courses"
          :loading="areCoursesLoading"
        >
        </v-select>
      </b-form-group>
      <page-separator title="Chapter Details" />
      <!-- Image Upload -->
      <b-form-group label="Chapter Image" label-for="image" label-class="form-label" class="row-align-items-center">
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isImgUploading">...</span>
            <b-img :src="chapter.image_url" class="img-fluid" width="40" alt="Image" v-else-if="chapter.image_url" />
            <i class="fas fa-image fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'image')"
            :isUploading="isImgUploading"
            placeholder="Select Chapter Image"
            :isRequired="!chapter.image_url"
          />
        </b-media>
      </b-form-group>

      <!-- Title -->
      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input id="title" placeholder="Enter Chapter Title" v-model="chapter.title" required />
      </b-form-group>

      <!-- Short Description -->
      <b-form-group label="Short Description" label-for="short_description" label-class="form-label">
        <b-form-textarea
          id="short_description"
          placeholder="Enter a brief description of the chapter"
          v-model="chapter.short_description"
          :rows="3"
        />
      </b-form-group>

      <!-- Long Description -->
      <b-form-group label="Detailed Description" label-for="long_description" label-class="form-label">
        <editor
          id="long_description"
          name="long_description"
          :init="{
            height: 300,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="chapter.long_description"
          placeholder="Enter detailed chapter description"
        />
      </b-form-group>

      <!-- Content Video -->
      <div class="row mb-4">
        <span class="form-label col-md-6">Chapter Video</span>
        <div class="col-md-6" style="display: flex; justify-content: end;">
          <b-btn
            v-if="!chapter.content_video"
            variant="primary"
            :class="{ 'mb-2': chapter.content_video }"
            style="width: 150px"
            @click.prevent="$refs.uploader.click()"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <i v-if="isUploadingVideoUrl" class="fas fa-circle-notch fa-spin"></i>
            <span v-else>Upload Video</span>
          </b-btn>
          <b-btn
            v-else
            variant="danger"
            :class="{ 'mb-2': chapter.content_video }"
            style="width: 150px"
            @click.prevent="removeVideo"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <span>Remove Video</span>
          </b-btn>
          <input type="file" ref="uploader" class="d-none" @change="handleFileChange" accept="video/*" />
        </div>
        <div v-if="chapter.content_video" class="d-flex align-items-center col">
          <video
            style="width: 100%; margin: auto;"
            :style="{ height: isMobileScreen ? '230px' : '450px' }"
            id="video-preview"
            controls
            :src="chapter.content_video"
          />
        </div>
      </div>

      <!-- Textbook Start Page -->
      <b-form-group label="Textbook Start Page" label-for="textbook_start_page" label-class="form-label">
        <b-form-input
          id="textbook_start_page"
          type="number"
          min="1"
          placeholder="Enter starting page number"
          v-model.number="chapter.textbook_start_page"
        />
      </b-form-group>

      <!-- Estimated Time -->
      <b-form-group label="Estimated Time (minutes)" label-for="estimated_time_minutes" label-class="form-label">
        <b-form-input
          id="estimated_time_minutes"
          type="number"
          min="1"
          placeholder="Enter estimated time in minutes"
          v-model.number="chapter.estimated_time_minutes"
        />
      </b-form-group>

      <!-- Quiz Selection -->
      <b-form-group label-for="quiz" label-class="form-label">
        <label class="form-label"
          >Quiz
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="'This quiz will be taken at the end of the Chapter.'"
          >
            info
          </i></label
        >
        <v-select
          id="quiz"
          class="form-control v-select-custom"
          label="title"
          v-model="chapter.quiz"
          :reduce="item => item.id"
          placeholder="Select Quiz (Optional)"
          :options="quizzes"
          :loading="areQuizzesLoading"
        >
        </v-select>
      </b-form-group>

      <!-- Chapter Order -->
      <b-form-group label="Chapter Order" label-for="order" label-class="form-label">
        <b-form-input
          id="order"
          type="number"
          min="1"
          placeholder="Enter chapter order number"
          v-model.number="chapter.order"
          required
        />
      </b-form-group>

      <b-button
        variant="outline-primary"
        class="mr-2"
        @click="openAddLessonModal"
        style="justify-self: end;display: block;"
      >
        <i class="fas fa-plus mr-1"></i> Add Lesson
      </b-button>

      <!-- Lessons Section -->
      <div class="form-group mb-4">
        <label class="form-label">Lessons</label>
        <draggable
          v-if="chapter.lessons && chapter.lessons.length > 0"
          v-model="chapter.lessons"
          class="mb-3"
          handle=".drag-handle"
          @end="updateLessonOrder"
        >
          <div
            v-for="(lesson, index) in chapter.lessons"
            :key="lesson.id ? `old-${index}` : `new-${index}`"
            class="d-flex align-items-center justify-content-center"
          >
            <div class="drag-handle" style="cursor: move;">
              <i class="fas fa-bars icon-16pt mr-2"></i>
            </div>
            <div class="card mb-2" style="width: 100%">
              <div class="card-body p-3 d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0" style="font-size: medium;">{{ lesson.title }}</h6>
                </div>
                <div>
                  <a
                    @click="editLesson(lesson, index)"
                    class="svg-icon mr-2"
                    v-b-popover.hover.left
                    :title="$t('generalMsgs.edit')"
                  >
                    <md-icon class="text-info">edit</md-icon>
                    <!-- <i class="far fa-edit text-info" /> -->
                  </a>

                  <a
                    href="#"
                    @click.prevent="removeLesson(index)"
                    v-b-popover.hover.left
                    :title="$t('generalMsgs.remove')"
                  >
                    <md-icon class="text-danger">delete</md-icon>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </draggable>
        <div v-else class="text-muted">No lessons added yet.</div>
      </div>

      <!-- Lesson Modal -->
      <lesson-modal
        ref="lessonModal"
        :modal-id="'lesson-modal'"
        :lesson="currentLesson"
        :is-editing="isEditingLesson"
        :quizzes="quizzes"
        :are-quizzes-loading="areQuizzesLoading"
        @submit="handleLessonSubmit"
      />

      <!-- Submit Button -->
      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Create' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import draggable from 'vuedraggable';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import Editor from '@tinymce/tinymce-vue';
import LessonModal from './components/LessonModal.vue';
import { FmvAvatar } from 'fmv-avatar';
import axios from 'axios';
import Page from '../../../components/Page.vue';

export default {
  components: {
    PageHeader,
    PageSeparator,
    ImageUploader,
    Editor,
    LessonModal,
    FmvAvatar,
    draggable,
  },

  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Chapter' : 'Add Chapter',
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      chapter: {
        course: null,
        title: '',
        short_description: '',
        long_description: '',
        content_video: '',
        image_url: '',
        textbook_start_page: null,
        estimated_time_minutes: null,
        quiz: null,
        order: null,
        lessons: [],
      },
      currentLesson: {
        title: '',
        description: '',
        content_video: '',
        order: 1,
      },
      isEditingLesson: false,
      currentLessonIndex: -1,
      courses: [],
      quizzes: [],
      uploadFile: { image: null },
      uploadPercent: { image: 0 },
      uploadCancelTokenSource: { image: null },
      isLoading: false,
      isImgUploading: false,
      areCoursesLoading: false,
      areQuizzesLoading: false,
      isUploadingVideoUrl: false,
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Chapters', to: { name: 'list-chapters' } },
        {
          text: this.$route.params.id ? 'Edit Chapter' : 'Add Chapter',
          active: true,
        },
      ];
    },
    // sortedLessons() {
    //   if (!this.chapter.lessons) return [];
    //   return [...this.chapter.lessons].sort((a, b) => a.order - b.order);
    // },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },

  methods: {
    ...mapActions('chapters', ['createChapter', 'updateChapter', 'getChapter', 'getChapterImageUploadPresignedUrl']),
    ...mapActions('courses', ['getLmsCourses']),
    ...mapActions('quizzes', ['getQuizzes']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),

    updateLessonOrder() {
      // Update the order field of each lesson based on their new position
      this.chapter.lessons = this.chapter.lessons.map((lesson, index) => ({
        ...lesson,
        order: index + 1,
      }));
    },

    openAddLessonModal() {
      this.currentLesson = {
        title: '',
        description: '',
        content_video: '',
        order: (this.chapter.lessons?.length || 0) + 1,
      };
      this.isEditingLesson = false;
      this.currentLessonIndex = -1;
      this.$refs.lessonModal.$bvModal.show('lesson-modal');
    },

    editLesson(lesson, index) {
      this.currentLesson = { ...lesson };
      this.isEditingLesson = true;
      this.currentLessonIndex = index;
      this.$refs.lessonModal.$bvModal.show('lesson-modal');
    },

    handleLessonSubmit(lessonData) {
      if (this.isEditingLesson && this.currentLessonIndex >= 0) {
        // Update existing lesson
        this.$set(this.chapter.lessons, this.currentLessonIndex, {
          ...this.chapter.lessons[this.currentLessonIndex],
          ...lessonData,
        });
      } else {
        // Add new lesson
        if (!this.chapter.lessons) {
          this.$set(this.chapter, 'lessons', []);
        }
        this.chapter.lessons.push({
          ...lessonData,
        });
      }
      this.$bvModal.hide('lesson-modal');
    },

    removeLesson(index) {
      this.$bvModal
        .msgBoxConfirm('Are you sure you want to delete this lesson?', {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.chapter.lessons.splice(index, 1);
            this.makeToast({ variant: 'success', msg: 'Lesson removed successfully' });
          }
        });
    },

    setImage(file, fileType) {
      this.uploadFile[fileType] = file;
      this.uploadImage(fileType);
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    async uploadVideo(file) {
      this.isUploadingVideoUrl = true;

      if (file.size > 25000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 25MB' });
        this.isUploadingVideoUrl = false;
        return;
      }
      if (!['video/mp4', 'video/webm'].includes(file.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be mp4 or webm' });

        this.isUploadingVideoUrl = false;
        return;
      }

      try {
        const urlResp = await this.getChapterImageUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.chapter.content_video = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.isUploadingVideoUrl = false;
        return;
      }
      this.isUploadingVideoUrl = false;
    },
    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isImgUploading = uploadType === 'image';

      try {
        const urlResp = await this.getChapterImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
          upload_type: 'images',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'image') {
          this.chapter.image_url = urlResp.data.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'image') this.isImgUploading = false;
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const chapterData = { ...this.chapter };

        if (this.$route.params.id) {
          await this.updateChapter({
            id: this.$route.params.id,
            data: chapterData,
          });
          this.makeToast({ variant: 'success', msg: 'Chapter updated successfully' });
          this.$router.push({ name: 'list-chapters' });
        } else {
          await this.createChapter(chapterData);
          this.makeToast({ variant: 'success', msg: 'Chapter created successfully' });
          this.$router.push({ name: 'list-chapters' });
        }
      } catch (error) {
        console.error('Error saving chapter:', error);
        this.makeToast({
          variant: 'danger',
          msg: error.response?.data?.message || 'Failed to save chapter. Please try again.',
        });
      } finally {
        this.isLoading = false;
      }
    },
    removeVideo() {
      this.chapter.content_video = '';
    },
    async loadData() {
      if (this.$route.params.id) {
        try {
          const chapter = await this.getChapter(this.$route.params.id);
          this.chapter = { ...chapter.data };
        } catch (error) {
          console.error('Error loading chapter:', error);
          this.makeToast({ variant: 'danger', msg: 'Failed to load chapter data' });
          this.$router.push({ name: 'list-chapters' });
        }
      }

      // Load courses and quizzes for dropdowns
    },
    async fetchQuizzes() {
      this.areQuizzesLoading = true;
      try {
        const response = await this.getQuizzes();
        this.quizzes = response.data || [];
      } catch (error) {
        console.error('Error loading quizzes:', error);
        this.$snotify.error('Failed to load quizzes');
      } finally {
        this.areQuizzesLoading = false;
      }
    },
    async fetchCourses() {
      this.areCoursesLoading = true;
      try {
        const response = await this.getLmsCourses();
        this.courses = response.data || [];
      } catch (error) {
        console.error('Error loading courses:', error);
        this.$snotify.error('Failed to load courses');
      } finally {
        this.areCoursesLoading = false;
      }
    },
  },

  async mounted() {
    await this.loadData();
    await this.fetchQuizzes();
    await this.fetchCourses();
  },
};
</script>
